import BuildInstallButton from '@/components/build/build-install-button';
import BuildCancelButton from '@/components/build/build-cancel-button';
import BuildLogsSection from '@/components/build/build-logs-section';
import BuildStatusBadge from '@/components/build/build-status-badge';
import { BuildSettingChips, BuildSettingUrls, BuildTechLine } from '@/components/build/build-settings-view';
import { getBuildSettingChips, getBuildSettingUrls, getBuildTechParts } from '@/components/build/build-settings';
import { resolveBuildDisplayIconUrl } from '@crave/config/apk-hiding';
import type { IBuild } from '@/entities/build.entity';
import type { IBuildLogLine } from '@/entities/build-live.entity';
import { isActiveBuild } from '@/entities/build.entity';
import { $profile } from '@/entities/profile-entity';
import { cn } from '@/lib/utils';
import { useUnit } from 'effector-react';
import { BoxIcon, CalendarIcon } from 'lucide-react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

function getBuildAccentClass(build: IBuild) {
	if (build.cancelled) return 'border-l-muted-foreground/40';
	if (build.isBuilding || build.inQueue) return 'border-l-chart-5';
	if (build.successBuilded) return 'border-l-chart-2';
	return 'border-l-destructive';
}

interface BuildCardProps {
	build: IBuild;
	className?: string;
	onCancel?: () => void;
	onInstalled?: () => void;
	adminLogs?: boolean;
	readOnly?: boolean;
	fetchBuildLogs?: (buildId: string) => Promise<{ lines: IBuildLogLine[]; failureMessage?: string }>;
}

export default function BuildCard({
	build,
	className,
	onCancel,
	onInstalled,
	adminLogs = false,
	readOnly = false,
	fetchBuildLogs,
}: BuildCardProps) {
	const profile = useUnit($profile);
	const isAdmin = profile?.role === 'admin';
	const title = build.appName || build.id;
	const showInstall = !readOnly && Boolean(build.successBuilded);
	const showDualInstall = Boolean(isAdmin && build.hasApk && build.hasSeparateRawApk);
	const showCancel = !readOnly && isActiveBuild(build) && build.canCancel;
	const showFailureLogs = Boolean(build.hasBuildLog) && !isActiveBuild(build);
	const chips = getBuildSettingChips(build).filter(chip => isAdmin || chip.key !== 'photos');
	const urls = getBuildSettingUrls(build);
	const techParts = getBuildTechParts(build);
	const displayIconUrl = resolveBuildDisplayIconUrl(build);

	return (
		<div className={cn('rounded-xl border border-l-2 border-border bg-card p-3 shadow-sm', getBuildAccentClass(build), className)}>
			<div className='flex items-start gap-3'>
				<div className='relative size-12 shrink-0 overflow-hidden rounded-xl border border-border bg-muted/40'>
					{displayIconUrl ? (
						<img src={displayIconUrl} alt={title} className='size-full object-cover' />
					) : (
						<div className='flex size-full items-center justify-center text-muted-foreground'>
							<BoxIcon size={20} />
						</div>
					)}
				</div>

				<div className='min-w-0 flex-1'>
					<div className='flex items-start justify-between gap-2'>
						<div className='min-w-0'>
							<p className='truncate font-medium'>{title}</p>
							<p className='truncate text-xs text-muted-foreground'>#{build.id}</p>
							{build.testBuild || build.skipEncrypt ? (
								<div className='mt-1 flex flex-wrap gap-1'>
									{build.testBuild ? (
										<span className='inline-flex items-center rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground'>
											DEBUG
										</span>
									) : null}
									{build.skipEncrypt ? (
										<span className='inline-flex items-center rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground'>
											TEST
										</span>
									) : null}
								</div>
							) : null}
						</div>
						<BuildStatusBadge build={build} />
					</div>
				</div>
			</div>

			<div className='mt-3 flex flex-col gap-2'>
				<BuildSettingChips chips={chips} />
				<BuildSettingUrls urls={urls} />
				<BuildTechLine parts={techParts} />
			</div>

			<div className='mt-3 flex flex-col gap-2 border-t border-border/60 pt-3'>
				<div className='flex items-center justify-between gap-2'>
					<div className='min-w-0 flex items-center gap-2 text-xs text-muted-foreground'>
						<span className='inline-flex items-center gap-1.5'>
							<CalendarIcon size={12} className='opacity-50' />
							<TimeAgo date={build.createdAt} formatter={formatter} />
						</span>
						<span className='opacity-40'>·</span>
						<span className='truncate'>{build.clientsCount} клиентов</span>
					</div>
					{showInstall || showCancel ? (
						<div className='flex shrink-0 flex-wrap items-center justify-end gap-2'>
							{showCancel ? <BuildCancelButton build={build} onCancelled={onCancel} /> : null}
							{showInstall ? (
								showDualInstall ? (
									<>
										<BuildInstallButton
											build={build}
											variant={build.hasEncryptApk ? 'encrypt' : undefined}
											label={build.hasEncryptApk ? 'Encrypt' : 'Dropper'}
											onInstalled={onInstalled}
										/>
										<BuildInstallButton
											build={build}
											variant='raw'
											label={build.skipEncrypt ? 'Payload' : 'Raw'}
											onInstalled={onInstalled}
										/>
									</>
								) : (
									<BuildInstallButton build={build} onInstalled={onInstalled} />
								)
							) : null}
						</div>
					) : null}
				</div>
				{build.successBuilded && build.apkZipPassword ? (
					<p className='text-xs text-muted-foreground'>
						Пароль архива: <span className='font-mono font-medium text-foreground'>{build.apkZipPassword}</span>
					</p>
				) : null}
				{showFailureLogs ? (
					<BuildLogsSection build={build} admin={adminLogs} fetchLogs={fetchBuildLogs} />
				) : null}
			</div>
		</div>
	);
}
